import React, { useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import useLiveUsers from 'hooks/useLiveUsers';
import useCountUp from 'hooks/useCountUp';
import AnalyticsErrorState from 'pages/Analytics/components/AnalyticsErrorState/AnalyticsErrorState';
import { TEALiveUsersBucket } from 'interface/templated-experience/analytics.interface';
import './LiveAnalyticsDashboard.scss';

const CLASS_MAIN = 'live-analytics';
const CLASSES = {
  ROOT: CLASS_MAIN,
  SECTION: `${CLASS_MAIN}__section`,
  SECTION_TITLE: `${CLASS_MAIN}__section-title`,
  CARD: `${CLASS_MAIN}__card`,
  LEFT: `${CLASS_MAIN}__left`,
  TITLE: `${CLASS_MAIN}__title`,
  VALUE: `${CLASS_MAIN}__value`,
  SUBTITLE: `${CLASS_MAIN}__subtitle`,
  RIGHT: `${CLASS_MAIN}__right`,
  CHART: `${CLASS_MAIN}__chart`,
};

// Section heading — this is the first (users-related) section of the live view.
const SECTION_TITLE = 'Users';

// Show roughly one x-axis label every TICK_EVERY buckets so 40 bars don't collide.
const TICK_EVERY = 8;

/** Format an epoch-seconds bucket start as a HH:MM:SS clock label. */
function clockLabel(epochSeconds: number): string {
  return new Date(epochSeconds * 1000).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

type BarDatum = { name: string; value: number };

/**
 * Owns useCountUp so its per-frame setState only re-renders this node, not the
 * surrounding dashboard (chart included).
 */
function AnimatedLiveCount({ target }: { target: number }) {
  const displayedValue = useCountUp(target);
  return <div className={CLASSES.VALUE}>{displayedValue.toLocaleString()}</div>;
}

const LiveAnalyticsDashboard = () => {
  const { liveUsers, isLoading, isError, refetch } = useLiveUsers();

  const { barData, tickValues, windowMinutes } = useMemo(() => {
    // Sort a defensive copy chronologically — tick sampling and the "newest
    // bucket" behavior both depend on order, and this is shared query data.
    const series: TEALiveUsersBucket[] = [...(liveUsers?.series ?? [])].sort((a, b) => a.t - b.t);
    const data: BarDatum[] = series.map((b) => ({ name: clockLabel(b.t), value: b.count }));
    const ticks = data.filter((_, i) => i % TICK_EVERY === 0).map((d) => d.name);
    const minutes = liveUsers ? Math.round(liveUsers.windowSeconds / 60) : 0;
    return { barData: data, tickValues: ticks, windowMinutes: minutes };
  }, [liveUsers]);

  if (isError) {
    return (
      <div className={CLASSES.ROOT}>
        <div className={CLASSES.SECTION}>
          <div className={CLASSES.SECTION_TITLE}>{SECTION_TITLE}</div>
          <div className={CLASSES.CARD}>
            <AnalyticsErrorState onRetry={() => refetch()} compact />
          </div>
        </div>
      </div>
    );
  }

  const showSkeleton = isLoading && !liveUsers;

  return (
    <div className={CLASSES.ROOT}>
      <div className={CLASSES.SECTION}>
        <div className={CLASSES.SECTION_TITLE}>{SECTION_TITLE}</div>
        <div className={CLASSES.CARD}>
          <div className={CLASSES.LEFT}>
            <div className={CLASSES.TITLE}>Live users</div>
            {showSkeleton ? <Skeleton width={96} height={40} /> : <AnimatedLiveCount target={liveUsers?.live ?? 0} />}
            <div className={CLASSES.SUBTITLE}>
              {showSkeleton ? <Skeleton width={160} height={14} /> : `Active in the last ${windowMinutes} min`}
            </div>
          </div>

          <div className={CLASSES.RIGHT}>
            <div className={CLASSES.CHART}>
              {showSkeleton ? (
                <Skeleton height={220} />
              ) : (
                <ResponsiveBar
                  data={barData}
                  keys={['value']}
                  indexBy="name"
                  margin={{ top: 10, right: 10, bottom: 40, left: 40 }}
                  padding={0.2}
                  valueScale={{ type: 'linear' }}
                  indexScale={{ type: 'band', round: true }}
                  colors={'#0851df'}
                  enableLabel={false}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 0,
                    tickPadding: 8,
                    tickValues,
                    legend: `Users per ${liveUsers?.bucketSeconds ?? 15}s`,
                    legendPosition: 'middle',
                    legendOffset: 34,
                  }}
                  axisLeft={{
                    tickSize: 0,
                    tickPadding: 5,
                    tickValues: 4,
                    legend: 'Active users',
                    legendPosition: 'middle',
                    legendOffset: -36,
                  }}
                  isInteractive={true}
                  animate={true}
                  motionConfig="gentle"
                  role="img"
                  ariaLabel="Active users per interval over the last 10 minutes"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveAnalyticsDashboard;
